import createClient from 'openapi-fetch';
import type { paths } from './generated/schema';
import { Config } from '#lib/constants/config.js';
import { withServerErrorRetry } from '#lib/utils/withServerErrorRetry/withServerErrorRetry.js';

export const createApiClient = (fetchFn?: typeof globalThis.fetch) =>
	createClient<paths>({
		baseUrl: Config.WebsiteApiUrl,
		fetch: withServerErrorRetry(fetchFn ?? globalThis.fetch),
	});

type ApiErrorPayload = {
	errors?: { code?: string; message?: string }[];
};

export class ApiResponseError extends Error {
	readonly status: number;

	constructor(status: number, message: string) {
		super(message);
		this.name = 'ApiResponseError';
		this.status = status;
	}
}

/**
 * Unwraps an openapi-fetch `{ data, error, response }` result: returns `data`
 * when present, otherwise throws an `ApiResponseError` carrying the response
 * status and the first `errors[].message` from the error payload, if any.
 */
export const responseData = <TData>({
	data,
	error,
	response,
}: {
	data?: TData;
	error?: ApiErrorPayload;
	response: Response;
}): TData => {
	if (data !== undefined) {
		return data;
	}

	const message = error?.errors?.[0]?.message ?? response.statusText;

	throw new ApiResponseError(response.status, message);
};
